import React, { useState } from 'react'
import { cn } from '@/lib/utils'
import { bannerTextStyle } from '@/lib/styleKit'
import type { FeedbackMineItem } from '@/types/feedback'
import ReportDetailModal from './ReportDetailModal'
import { TONE_CLASS, formatReportDate, kindLabel, orderForDisplay, previewText, statusTone } from './myReportsLogic'

interface ReportsPanelProps {
  items: FeedbackMineItem[] | null
  failed: boolean
}

/**
 * The reporter's own submissions (brief AD7), shown in place of a form when
 * the "Reports" card is selected. Each row previews the description to 30
 * characters; clicking it opens the full report in a modal. Rows whose
 * status moved since the user last opened this card are marked, so the
 * badge on the card and the list agree about what is new.
 */
const ReportsPanel: React.FC<ReportsPanelProps> = ({ items, failed }) => {
  const [selected, setSelected] = useState<FeedbackMineItem | null>(null)

  if (failed) {
    return (
      <p className="text-red-600 dark:text-red-400" style={bannerTextStyle}>
        Could not load your reports. Try again in a moment.
      </p>
    )
  }
  if (items === null) {
    return (
      <p className="text-muted-foreground dark:text-[#8a8f98]" style={bannerTextStyle}>
        Loading your reports…
      </p>
    )
  }
  if (items.length === 0) {
    return (
      <div className="py-8 text-center">
        <p className="text-foreground dark:text-[#f7f8f8]" style={{ fontSize: '13px', fontWeight: 590 }}>
          Nothing sent yet
        </p>
        <p className="mt-1 text-muted-foreground dark:text-[#8a8f98]" style={bannerTextStyle}>
          Bugs, feedback and ratings you send will show up here with their status.
        </p>
      </div>
    )
  }

  const ordered = orderForDisplay(items)
  return (
    <div>
      <p className="text-muted-foreground dark:text-[#8a8f98] mb-3" style={bannerTextStyle}>
        {items.length} report{items.length === 1 ? '' : 's'}. Click one to see everything you sent. Status updates from the support team appear here.
      </p>
      <ul className="divide-y divide-border/50 dark:divide-white/[0.06] rounded-md border border-border/60 dark:border-white/[0.08]">
        {ordered.map((item) => {
          const preview = previewText(item.description)
          return (
            <li
              key={item.id}
              className={cn('flex items-center gap-3 px-3 py-2.5', item.is_unseen && 'bg-[#5e6ad2]/[0.04] dark:bg-[#5e6ad2]/[0.08]')}
              style={{ fontSize: '13px' }}
            >
              <span className="w-24 shrink-0 text-xs text-muted-foreground dark:text-[#8a8f98]">{kindLabel(item)}</span>
              <button
                type="button"
                onClick={() => setSelected(item)}
                className="min-w-0 flex-1 text-left rounded-sm hover:bg-muted/40 dark:hover:bg-white/[0.04] -mx-1 px-1 py-0.5"
                aria-label={preview || item.title}
              >
                <span className="flex items-center gap-2 min-w-0">
                  {item.is_unseen && (
                    <span className="inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-[#5e6ad2]" aria-label="Updated" />
                  )}
                  {item.reference_code && (
                    <span className="shrink-0 font-mono text-xs text-muted-foreground dark:text-[#8a8f98]">{item.reference_code}</span>
                  )}
                  <span className="truncate font-medium text-foreground dark:text-[#f7f8f8]">{item.title}</span>
                </span>
                {preview && (
                  <span className="block truncate text-xs text-muted-foreground dark:text-[#8a8f98]" title={item.description ?? undefined}>
                    {preview}
                  </span>
                )}
              </button>
              <span
                className={cn('shrink-0 rounded-md px-2 py-0.5 text-xs font-medium', TONE_CLASS[statusTone(item.user_status)])}
                title={item.resolved_at ? `Resolved ${formatReportDate(item.resolved_at)}` : undefined}
              >
                {item.user_status}
              </span>
              <span className="hidden sm:inline w-24 shrink-0 text-right text-xs text-muted-foreground dark:text-[#8a8f98]">
                {formatReportDate(item.created_at)}
              </span>
            </li>
          )
        })}
      </ul>
      <ReportDetailModal report={selected} onClose={() => setSelected(null)} />
    </div>
  )
}

export default ReportsPanel
